/**
 * CareCal Component Variants
 * Centralized component variant definitions using CVA and design tokens
 */

import { cva } from 'class-variance-authority'

/**
 * Button Variants
 * Consistent button styling across the application
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold uppercase tracking-wide transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blueviolet focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-blueviolet text-white rounded-full shadow-sm hover:bg-blueviolet-light hover:shadow-md active:bg-blueviolet-dark',
        destructive: 'bg-error text-white rounded-full shadow-sm hover:bg-error-dark focus-visible:ring-tigerlily',
        outline: 'border-2 border-blueviolet bg-white text-blueviolet rounded-full hover:bg-lilac-100',
        secondary: 'border-2 border-blueviolet bg-white text-blueviolet rounded-full shadow-sm hover:bg-lilac-100 hover:shadow-md',
        ghost: 'text-night-500 hover:text-night-300 hover:underline normal-case font-normal',
        link: 'text-blueviolet underline-offset-4 hover:underline hover:text-blueviolet-light normal-case',
      },
      size: {
        default: 'h-11 px-6 py-3 text-sm min-h-[44px]', // Ensure 44px minimum touch target
        sm: 'h-11 px-4 py-2 text-sm min-h-[44px]', // Increased from h-9 for touch accessibility
        lg: 'h-12 px-8 py-3 text-base min-h-[44px]',
        icon: 'h-11 w-11 min-h-[44px] min-w-[44px]', // Ensure square touch target
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

/**
 * Badge Variants
 * Status badges and labels
 */
export const badgeVariants = cva(
  'inline-flex items-center rounded-full max-h-[24px] px-3 py-1 text-xs font-body tracking-normal font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-blush-500 focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'bg-blueviolet text-white border-transparent',
        secondary: 'bg-lilac-900 text-info border-transparent',
        success: 'bg-success text-success-dark border-transparent',
        warning: 'bg-warning text-white border-transparent',
        destructive: 'bg-error text-white border-transparent',
        outline: 'border-2 border-lilac-900 text-blueviolet bg-white',
        // Booking status variants
        pending: 'bg-lilac-900 text-info border-transparent',
        accepted: 'bg-success-light text-success-dark',
        declined: 'bg-night-100 text-night-700 border-transparent',
        confirmed: 'bg-success text-success-dark border-transparent',
        completed: 'bg-blueviolet text-white border-transparent',
        canceled: 'bg-error-light text-error-dark border-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

/**
 * Card Variants
 * Container components with different styles
 */
export const cardVariants = cva(
  'rounded-lg bg-cream border border-blush-500/20 transition-shadow duration-200',
  {
    variants: {
      variant: {
        default: 'shadow-xs hover:shadow-sm',
        elevated: 'shadow-sm hover:shadow-md',
        flat: 'shadow-none',
        interactive: 'shadow-xs hover:shadow-md cursor-pointer',
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        default: 'p-6',
        lg: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'default',
    },
  }
)

/**
 * Input Variants
 * Form input styling
 */
export const inputVariants = cva(
  'flex w-full rounded-xl border bg-white transition-all duration-200 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-night-300 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-night-100',
  {
    variants: {
      variant: {
        default:
          'border-lilac-900 focus-visible:ring-2 focus-visible:ring-blueviolet',
        error:
          'border-error focus-visible:border-error focus-visible:ring-2 focus-visible:ring-error-dark',
      },
      inputSize: {
        default: 'h-11 px-4 py-3 text-base min-h-[44px]', // Ensure 44px minimum touch target
        sm: 'h-11 px-3 py-2 text-sm min-h-[44px]', // Increased from h-9 for touch accessibility
        lg: 'h-12 px-4 py-3 text-base min-h-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  }
)

/**
 * Skeleton Variants
 * Loading state placeholders
 */
export const skeletonVariants = cva('animate-pulse rounded-xl', {
  variants: {
    variant: {
      default: 'bg-lilac-100',
      light: 'bg-lilac-100',
      card: 'bg-lilac-500',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

/**
 * Alert/Toast Variants
 * Notification styling
 */
export const alertVariants = cva(
  'relative w-full rounded-xl border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-white border-lilac-900 text-night-900',
        success: 'bg-success-light border-success-dark text-success-dark',
        warning: 'bg-warning-light border-warning text-warning-dark',
        error: 'bg-error-light border-error text-error-dark',
        info: 'bg-info-light border-info text-info-dark',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

/**
 * Status Indicator Variants
 * Small status dots/indicators
 */
export const statusIndicatorVariants = cva('inline-block h-2 w-2 rounded-full', {
  variants: {
    status: {
      available: 'bg-success',
      unavailable: 'bg-gray-400',
      pending: 'bg-blush-500',
      confirmed: 'bg-success',
      canceled: 'bg-error',
      completed: 'bg-gray-600',
    },
  },
  defaultVariants: {
    status: 'available',
  },
})

/**
 * Text Variants
 * Typography helpers
 */
export const textVariants = cva('', {
  variants: {
    variant: {
      h1: 'font-display text-[32px] leading-[38px] font-semibold',
      h2: 'font-display text-2xl leading-[30px] font-semibold',
      h3: 'font-display text-xl leading-[26px] font-semibold',
      h4: 'font-display text-lg leading-6 font-medium',
      bodyLarge: 'font-body text-base leading-6',
      body: 'font-body text-sm leading-[21px]',
      bodySmall: 'font-body text-xs leading-[18px]',
      caption: 'font-body text-[11px] leading-4 font-medium',
    },
    color: {
      default: 'text-gray-900',
      muted: 'text-gray-600',
      light: 'text-gray-400',
      primary: 'text-blush-500',
      error: 'text-error',
      success: 'text-success',
    },
  },
  defaultVariants: {
    variant: 'body',
    color: 'default',
  },
})
